import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core'
import { Observable, of, Subject, throwError } from 'rxjs'
import { LbHttpService } from '../lb-http.service'
import { LocalStorageService } from '../utils/local-storage.service'
import { MatDialog } from '@angular/material/dialog'
import { ErrorService } from '../utils/error.service'
import { fromPromise } from 'rxjs/internal-compatibility'
import { catchError, first, map, retryWhen, switchMap, timeout } from 'rxjs/operators'
import { ConfigService } from '../config.service'
import { HttpClient } from '@angular/common/http'
import { EventSourcePolyfill, NativeEventSource } from 'event-source-polyfill'
import { ConfirmDialogComponent } from '../../dialog/confirm-dialog/confirm-dialog.component'
import { L10N_LOCALE, L10nLocale } from 'angular-l10n'
import { LbTranslationService } from '@affluences/lb-utils-front'
import { Endpoint } from '@affluences/lb-types'

const EventSource = NativeEventSource || EventSourcePolyfill
let currentsEndpointSse: EventSourcePolyfill | NativeEventSource = null

@Injectable( {
    providedIn: 'root'
} )
export class EndpointService implements OnDestroy {

    endpointStateSubscription: any
    endpointStateSseRetry: number = 0

    private endpointStateLoaded: boolean = false
    private endpointIsSubscribing: boolean = false
    private endpointStateEvent: Subject<any> = new Subject<any>()
    public $endpointStateEvent: Observable<any> = new Observable<any>()
    public endpointState: any = {}

    constructor (
        @Inject( L10N_LOCALE ) public locale: L10nLocale,
        private zone: NgZone,
        private http: HttpClient,
        public configService: ConfigService,
        public lbHttpService: LbHttpService,
        public lbTranslationService: LbTranslationService,
        public localStorageService: LocalStorageService,
        public dialog: MatDialog,
        private errorService: ErrorService
    ) {
        this.$endpointStateEvent = this.endpointStateEvent.asObservable()
    }

    ngOnDestroy (): void {
        this.unsubscribe()
    }

    async loadEndpointStateIfNotLoaded () {
        if ( !this.endpointStateLoaded ) {
            await this.initSubScribe()
        } else {
            this.sendEndpointStates()
        }
    }

    public sendEndpointStates () {
        if ( Object.keys( this.endpointState ).length > 0 ) {
            this.endpointStateEvent.next( this.endpointState )
        }
    }

    public unsubscribe () {
        if ( this.endpointStateSubscription ) {
            this.endpointStateLoaded = false
            this.endpointStateSubscription.unsubscribe()
            this.endpointStateSubscription = null
        }

        if ( currentsEndpointSse ) {
            currentsEndpointSse.close()
            currentsEndpointSse = null
        }
    }

    async initSubScribe () {

        if (this.endpointIsSubscribing) {
            this.errorService.logDebug( 'Endpoint is already subscribing' )
            return
        }

        this.unsubscribe()

        this.endpointIsSubscribing = true
        this.endpointStateLoaded = true

        const endpointId = this.localStorageService.getSelectedEndpoint().endpointId

        try {
             // GET TICKETS WITH SSE
            this.endpointStateSubscription = (await this.httpGetEndpointSse( endpointId )).subscribe( async ( res ) => {
                this.errorService.logDebug( 'Event on endpoint SSE', res )
                this.endpointStateSseRetry = 0
                if ( res ) {
                    this.updateEndpointStateFromEvent( res )
                }
            }, async ( error ) => {
                this.errorService.logError( 'Error while getting selected endpoint by SSE', error )
    
                if ( error === 'SSE_TEMPORARY_ERROR' || error === 'SSE_CLOSED' ) {
                    this.endpointStateSseRetry++
    
                    if ( this.endpointStateSseRetry < 5 ) {
                        return this.initSubScribe()
                    } else {
                        this.unsubscribe()
                        this.errorService.reloadInterface()
                    }
                }
            } )
        }
        finally {
            this.endpointIsSubscribing = false
        }
    }

    updateEndpointStateFromEvent ( event ) {
        if ( event.resource === 'ENDPOINT' ) {
            if ( event.actionName === 'ALL_DATA' ) {
                if ( event.data && event.data ) {
                    this.endpointState = event.data
                    this.sendEndpointStates()
                }
            } else if ( event.actionName === 'ERROR' ) {
                this.unsubscribe()
                const errorName = event.data.error

                const data: any = {
                    width: '80vw',
                    data: {
                        text: '',
                        confirm: this.lbTranslationService.translateFromKey( 'endpoint_confirmDialog_confirm_ok', { lang: this.locale.language } )
                    }
                }

                switch ( errorName ) {
                    case 'ENDPOINT_ID_NOT_FOUND':
                        data.data.text = this.lbTranslationService.translateFromKey( 'endpoint_endpointService_popupError_endpointIdNotFound_text', { lang: this.locale.language } )
                        break
                    default:
                        data.data.text = this.lbTranslationService.translateFromKey( 'endpoint_endpointService_popupError_unknwonError_text', { lang: this.locale.language } )
                        break
                }

                this.dialog.open( ConfirmDialogComponent, data )
            } else {
                if ( event.data && event.data.endpoint && typeof (event.data.endpoint.state) !== 'undefined' ) {
                    if ( !this.endpointState ) {
                        this.endpointState = {}
                    }
                    this.endpointState.state = event.data.state
                    this.sendEndpointStates()
                }
            }

        } else if ( event.resource === 'ENDPOINT_TIMESLOT' ) {
            this.errorService.logDebug( 'Endpoint timeslots updated' )
        }
    }

    httpGetQueueEndpoints ( queueId ): Observable<any> {
        let url = this.configService.getQueueEndpoints
        url = url.replace( '{queueId}', queueId )

        return fromPromise( this.lbHttpService.generateHeaderForLineberty( false, false, 'validation' ) ).pipe(
            timeout( this.configService.defaultHttpTimeout ),
            switchMap( ( httpOptions: any ) => {
                return this.http.get( url, httpOptions )
            } ),
            first(),
            retryWhen( this.lbHttpService.genericRetryStrategy( {} ) ),
            catchError( ( error ) => {
                this.errorService.logError( 'Error while getting endpoint', error )
                return throwError( error )
            } )
        )
    }

    getEndpointConfiguration ( endpointId ): Observable<any> {
        const tmpEndpointConfig = this.localStorageService.getSelectedEndpointConfiguration()
        if ( tmpEndpointConfig && tmpEndpointConfig.loaded && tmpEndpointConfig.endpointId === endpointId ) {
            return of( tmpEndpointConfig )
        } else {
            return this.httpGetEndpointConfiguration( endpointId ).pipe(
                switchMap( (endpointConfig: any): Observable<any> => {
                    const endpointConfigurationRes = { endpointId: endpointId, loaded: true, sort: null, filter: null }
                    if (
                        endpointConfig
                        && endpointConfig.sort
                        && typeof( endpointConfig.sort ) === 'object'
                    ) {
                        endpointConfigurationRes.sort = endpointConfig.sort
                    }

                    if (
                        endpointConfig
                        && endpointConfig.filter
                        && typeof( endpointConfig.filter ) === 'object'
                    ) {
                        endpointConfigurationRes.filter = endpointConfig.filter
                    }
                    this.localStorageService.setSelectedEndpointConfiguration( endpointConfigurationRes )
                    return of(endpointConfigurationRes)
                }),
                catchError( (error) => {
                    this.errorService.logError( 'Error while getting endpoint config', error )
                    return throwError( error )
                })
            )
        }
    }

    httpGetEndpointConfiguration ( endpointId ): Observable<any> {
        let url = this.configService.getEndpointConfig
        url = url.replace( '{endpointId}', endpointId )

        return fromPromise( this.lbHttpService.generateHeaderForLineberty( true, false, 'validation' ) ).pipe(
            timeout( this.configService.defaultHttpTimeout ),
            switchMap( ( httpOptions: any ) => {
                return this.http.get( url, httpOptions )
            } ),
            first(),
            retryWhen( this.lbHttpService.genericRetryStrategy( {} ) ),
            catchError( ( error ) => {
                this.errorService.logError( 'Error while getting endpoint', error )
                return throwError( error )
            } )
        )
    }

    httpGetEndpoint ( endpointId ) {
        let url = this.configService.getEndpoint
        url = url.replace( '{endpointId}', endpointId )

        return fromPromise( this.lbHttpService.generateHeaderForLineberty( false, false, 'validation' ) ).pipe(
            timeout( this.configService.defaultHttpTimeout ),
            switchMap( ( httpOptions: any ) => {
                return this.http.get( url, httpOptions )
            } ),
            first(),
            retryWhen( this.lbHttpService.genericRetryStrategy( {} ) ),
            catchError( ( error ) => {
                this.errorService.logError( 'Error while getting endpoint', error )
                return throwError( error )
            } )
        )
    }

    async httpGetEndpointSse ( endpointId ): Promise<Observable<any>> {

        let url = this.configService.getEndpointSse
        url = url.replace( '{endpointId}', endpointId )
        const httpOptions = await this.lbHttpService.generateHeaderForLineberty( true, true, 'validation' )

        return Observable.create( ( observer ) => {
            currentsEndpointSse = new EventSourcePolyfill( this.lbHttpService.applyGetParamsToUrl( url, httpOptions.params ), {
                headers: httpOptions.headers
            } )
            currentsEndpointSse.onopen = () => {
                this.errorService.logDebug( 'Endpoint SSE connected' )
            }
            currentsEndpointSse.onmessage = ( event ) => {
                this.zone.run( () => observer.next( JSON.parse( event.data ) ) )
            }
            currentsEndpointSse.onerror = ( error ) => {
                this.errorService.logError( 'Error on endpoint sse', error )
                this.zone.run( () => observer.error( 'SSE_CLOSED' ) )
            }
        } )
    }

    public httpOpenEndpoint ( open, mode?, saveInLocalStorage = false ): any {
        let url = this.configService.openEndpoint
        url = url.replace( '{endpointId}', this.localStorageService.getSelectedEndpoint()['endpointId'] )
        const body: any = { open: open }
        if (mode) body.mode = mode
        if (
            (open && this.localStorageService.getSelectedEndpoint().state === 0)
            || (!open && this.localStorageService.getSelectedEndpoint().state === 2)
        ) {
            return of( true )
        } else {
            return fromPromise( this.lbHttpService.generateHeaderForLineberty( true, false, 'validation' ) ).pipe(
                timeout( this.configService.defaultHttpTimeout ),
                switchMap( ( httpOptions: any ) => {
                    return this.http.put( url, body, httpOptions )
                } ),
                map( ( endpoint ) => {
                    if ( saveInLocalStorage ) {
                        this.localStorageService.setSelectedEndpoint( endpoint )
                    }
                } ),
                first(),
                retryWhen( this.lbHttpService.genericRetryStrategy( {} ) ),
                catchError( ( error ) => {
                    this.errorService.logError( 'Error while opening or closing endpoint', error )
                    return throwError( error )
                } )
            )
        }
    }

}

